import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { KeyRound, X, Delete, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { setAppUserPin, getStore, getUser, setUser } from "@/lib/api";

/**
 * Modal to create, change or remove the current user's 4-digit PIN.
 * Rules:
 *  - If the user has NO pin yet (pin_set === false), only asks for the new pin (twice).
 *  - If the user HAS a pin, asks for the current pin, then the new pin (twice).
 *  - "Quitar contraseña" wipes the pin (only visible when pin_set === true).
 */
export default function PinSetupModal({ open, onClose, onSaved }) {
  const store = getStore();
  const user = getUser();
  const pinSet = !!user?.pin_set;

  const [step, setStep] = useState(pinSet ? "current" : "new"); // current | new | confirm
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const reset = () => {
    setStep(pinSet ? "current" : "new");
    setCurrent(""); setNext(""); setConfirm(""); setBusy(false);
  };

  const close = () => { reset(); onClose?.(); };

  const activeBuf = step === "current" ? current : step === "new" ? next : confirm;
  const setActiveBuf = (val) => {
    if (step === "current") setCurrent(val);
    else if (step === "new") setNext(val);
    else setConfirm(val);
  };

  const press = (d) => {
    if (activeBuf.length >= 4 || busy) return;
    const v = activeBuf + d;
    setActiveBuf(v);
    // Auto-advance only when NOT in 'current' step — otherwise the user needs
    // a chance to also tap "Quitar contraseña" before we move to the new-pin screen.
    if (v.length === 4 && step !== "current") advance(v);
  };

  const back = () => setActiveBuf(activeBuf.slice(0, -1));

  const advance = async (val) => {
    if (step === "current") {
      // We don't validate here — the server will reject on save.
      setStep("new");
    } else if (step === "new") {
      setStep("confirm");
    } else {
      // confirm step: attempt save
      if (val !== next) {
        toast.error("Las contraseñas no coinciden");
        setConfirm("");
        setStep("new");
        setNext("");
        return;
      }
      await save(val);
    }
  };

  const save = async (finalPin) => {
    setBusy(true);
    try {
      const updated = await setAppUserPin(user.id, store.id, finalPin, pinSet ? current : null);
      setUser(updated);
      toast.success("Contraseña guardada");
      onSaved?.(updated);
      close();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar");
      // Reset back to the failing step
      if (e?.response?.status === 401) {
        setStep("current"); setCurrent(""); setNext(""); setConfirm("");
      } else {
        setStep("new"); setNext(""); setConfirm("");
      }
    } finally {
      setBusy(false);
    }
  };

  const removePin = async () => {
    if (!current || current.length !== 4) {
      toast.error("Escribe tu contraseña actual para quitarla");
      return;
    }
    setBusy(true);
    try {
      const updated = await setAppUserPin(user.id, store.id, "", current);
      setUser(updated);
      toast.success("Contraseña eliminada — entrarás directo");
      onSaved?.(updated);
      close();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo quitar");
    } finally { setBusy(false); }
  };

  if (!open) return null;

  const title = pinSet
    ? step === "current" ? "Contraseña actual"
    : step === "new" ? "Nueva contraseña"
    : "Confirma la nueva contraseña"
    : step === "new" ? "Crea tu contraseña" : "Confirma la contraseña";

  const subtitle = pinSet
    ? "Para cambiar tu contraseña primero valida la actual."
    : "Elige 4 dígitos. Te los pediremos solo cuando cierres sesión.";

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[60] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
        onClick={close}
        data-testid="pin-setup-modal"
      >
        <motion.div
          initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 30, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
          className="w-full max-w-sm bg-[#0a0a0a] border border-white/10 rounded-3xl p-8"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <KeyRound size={16} className="text-[#CCFF00]" />
              <span className="font-mono text-[10px] tracking-widest text-white/50 uppercase">
                {pinSet ? "Cambiar contraseña" : "Crear contraseña"}
              </span>
            </div>
            <button onClick={close} className="text-white/40 hover:text-white" data-testid="pin-setup-close">
              <X size={18} />
            </button>
          </div>

          <h2 className="font-display text-2xl tracking-tighter mb-1">{title}</h2>
          <p className="text-white/50 text-xs mb-6">{subtitle}</p>

          <div className="flex justify-center gap-3 mb-6">
            {[0, 1, 2, 3].map((i) => (
              <span
                key={i}
                className={`w-4 h-4 rounded-full transition-colors ${
                  i < activeBuf.length ? "bg-[#ccff00]" : "bg-white/15"
                }`}
                data-testid={`pin-setup-dot-${step}-${i}`}
              />
            ))}
          </div>

          <div className="grid grid-cols-3 gap-2">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
              <button
                key={n}
                onClick={() => press(String(n))}
                disabled={busy}
                className="py-4 rounded-xl border border-white/10 hover:bg-white/5 font-display text-2xl tracking-tighter disabled:opacity-40"
                data-testid={`pin-setup-key-${n}`}
              >
                {n}
              </button>
            ))}
            <span />
            <button
              onClick={() => press("0")}
              disabled={busy}
              className="py-4 rounded-xl border border-white/10 hover:bg-white/5 font-display text-2xl tracking-tighter disabled:opacity-40"
              data-testid="pin-setup-key-0"
            >
              0
            </button>
            <button
              onClick={back}
              disabled={busy}
              className="py-4 rounded-xl border border-white/10 hover:bg-white/5 flex items-center justify-center text-white/60 disabled:opacity-40"
              data-testid="pin-setup-back"
            >
              {busy ? <Loader2 size={18} className="animate-spin" /> : <Delete size={18} />}
            </button>
          </div>

          {pinSet && step === "current" && (
            <div className="mt-6 space-y-2">
              <button
                onClick={() => advance(current)}
                disabled={busy || current.length !== 4}
                className="w-full py-3 rounded-full bg-[#CCFF00] text-black font-mono text-xs uppercase tracking-widest hover:bg-white transition disabled:opacity-40"
                data-testid="pin-setup-continue"
              >
                Continuar
              </button>
              <button
                onClick={removePin}
                disabled={busy || current.length !== 4}
                className="w-full py-2.5 rounded-full border border-white/10 hover:border-red-500/50 text-white/50 hover:text-red-400 text-xs font-mono uppercase tracking-widest transition disabled:opacity-40 flex items-center justify-center gap-2"
                data-testid="pin-setup-remove"
              >
                <Trash2 size={12} /> Quitar contraseña
              </button>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
